import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { createClient } from '@/lib/supabase/client'

export function AuthCallbackPage() {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code')
      const confirmed = searchParams.get('confirmed')
      const next = searchParams.get('next') ?? '/pipeline'

      if (code) {
        const supabase = createClient()
        const { error } = await supabase.auth.exchangeCodeForSession(code)

        if (!error) {
          // Check if user has a team
          const {
            data: { user },
          } = await supabase.auth.getUser()

          if (user) {
            const { data: membership } = await supabase
              .from('memberships')
              .select('id')
              .eq('user_id', user.id)
              .single()

            // If no team, redirect to team setup (with confirmation notice if applicable)
            if (!membership) {
              navigate(confirmed ? '/team-setup?confirmed=true' : '/team-setup')
              return
            }
          }

          // If this was an email confirmation, add param to show success message
          if (confirmed) {
            const separator = next.includes('?') ? '&' : '?'
            navigate(`${next}${separator}confirmed=true`)
            return
          }

          navigate(next)
          return
        }
      }

      // Auth error
      setError('Authentication failed. Please try again.')
    }

    handleCallback()
  }, [navigate, searchParams])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
            Authentication Error
          </h1>
          <p className="text-zinc-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl bg-primary-500 animate-pulse">
          F
        </div>
        <p className="text-zinc-500">Completing sign in...</p>
      </div>
    </div>
  )
}
